import {
  Component,
  Input,
  ViewEncapsulation,
  input,
  computed,
  InputSignal,
  signal,
  Signal,
  WritableSignal,
  ViewChild,
  ElementRef,
  OnInit,
  Renderer2,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { CoreModule } from 'src/app/core/core.module';
import { TranslateService } from '@ngx-translate/core';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';

const DEBUG = false;

const log = (...args: any[]) => {
  if (DEBUG) {
    console.log(...args);
  }
};

const PADDING = 8;

@Component({
  selector: 'app-estimate-slider',
  standalone: true,
  styleUrl: './estimate-slider.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  template: `
    <div class="marks">
      @if (!notAvailable()) {
        <div class="label" [style]="this.labelStyles()">
          <div class="label-white"></div>
          <div class="label-arrow"></div>
          <div class="label-container">
            <span
              class="label-text"
              id="value-label"
              (click)="editValue($event)"
              >{{ value() | number: '1.0-0' }}</span
            >
            <input
              class="label-text hidden"
              id="value-input"
              appNumberOnly
              appInputAutosize
              #valueInput
              [value]="value()"
              pattern="[0-9]"
              (keyup.enter)="onEnterKey($event)"
            />
          </div>
        </div>
      }
      <div class="mark mark-left">
        @if (!notAvailable()) {
          <span [attr.data-min]="min()">{{ min() | number: '1.0-0' }}</span>
        }
      </div>
      @if (!notAvailable()) {
        @for (tier of tiers(); track tier; let i = $index) {
          <div
            class="mark"
            [attr.data-tier1]="tiers()[i]"
            [style]="this.tierStyles['tier' + i]"
          ></div>
        }
      }
      <div class="mark mark-right">
        @if (!notAvailable()) {
          <span [attr.data-max]="max()">{{ max() | number: '1.0-0' }}</span>
        }
      </div>
    </div>
    <mat-slider
      [min]="min()"
      [max]="max()"
      step="1"
      disableRipple
      [disabled]="notAvailable()"
      class="estimate-slider"
    >
      <input
        matSliderThumb
        #slider
        [ngModel]="value()"
        (ngModelChange)="updateValue($event)"
        class="slider-handle"
        [style.display]="notAvailable() ? 'none' : 'block'"
      />
    </mat-slider>
    @if (error() !== null) {
      <span class="error">{{ error() }}</span>
    }
  `,
  imports: [MatSliderModule, CommonModule, FormsModule, CoreModule],
})
export class EstimateSliderComponent implements OnChanges, OnInit {
  @ViewChild('valueInput') valueInput: ElementRef<HTMLInputElement>;

  value: WritableSignal<number> = signal(-1);

  id: InputSignal<any> = input(null);

  width: InputSignal<number> = input(300);

  min: InputSignal<number> = input(0);

  max: InputSignal<number> = input(0);

  tiers: InputSignal<number[]> = input([0, 0, 0]);

  initialSliderPosition: InputSignal<number> = input(0.75);

  tierStyles: Record<string, any> = {};

  error: WritableSignal<string | null> = signal(null);

  @Input()
  colors: string[] = [];

  public notAvailable: Signal<boolean> = computed(() => {
    const min = this.min();
    const max = this.max();
    const result = !min || !max || min < 0 || max < 0;
    return result;
  });

  labelStyles = signal({
    left: '8px',
  });

  @Input()
  onChange: (event: any) => void;

  constructor(
    private translateService: TranslateService,
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const hasChanged = (key: string) => {
      const obj = changes[key];
      if (obj && obj.currentValue.firstChange) {
        return false;
      }

      if (obj && isArray(obj.currentValue)) {
        const prev = obj.previousValue;
        const current = obj.currentValue;
        return isEqual(prev, current);
      }

      if (obj && obj.currentValue !== obj.previousValue) {
        return true;
      }

      return false;
    };
    log('estimate-slider.component.ngOnChanges  changes', changes);
    if (hasChanged('min') || hasChanged('max') || hasChanged('tiers')) {
      this.initialize();
    }
  }

  ngOnInit() {
    if (this.min() >= 0 && this.max() > 0) {
      this.initialize();
    }
  }

  private initialize() {
    const initValue =
      this.min() + (this.max() - this.min()) * this.initialSliderPosition();

    log(
      'estimate-slider.component.ngOnInit  min',
      this.min(),
      this.max(),
      this.initialSliderPosition(),
      initValue
    );

    this.updateValue(initValue);

    this.setMarks();
  }

  private setMarks() {
    log('estimate-slider.component.setMarks  tiers', this.tiers());
    const sliderBgEl = this.el.nativeElement.querySelector(
      'div.mdc-slider__track'
    );
    const notAvailable = this.notAvailable();
    log('estimate-slider.component.ngOnInit  notAvailable', notAvailable);
    const bgStr = this.getSliderBackground(notAvailable);
    if (DEBUG) {
      console.log('---------------------------');
      console.log('estimate-slider', this.id());
      console.log('     width', this.width());
      console.log('     min', this.min());
      console.log('     max', this.max());
      console.log('     tiers', this.tiers());
      console.log('     backgroundColor', bgStr);
    }
    sliderBgEl.style.background = bgStr;
    this.tiers().forEach((pct, index) => {
      const style = this.getMarkStyle(pct, index);
      this.tierStyles[`tier${index}`] = style;
    });
  }

  private getSliderBackground(notAvailable: boolean) {
    if (notAvailable) {
      log(
        'estimate-slider.component.getSliderBackground notAvailable!!!!!!!!!!!!!!!!!!!!!!!!!!!'
      );
      return '#666666';
    }

    // linear-gradient(to right, cyan 25%, palegoldenrod 25%, palegoldenrod 50%, green 50%, green 75%, blue 50%);
    let result = '';
    let prevPct = 0;
    let colorIndex = 0;
    this.tiers().forEach((pct, index) => {
      const currentPct = pct * 100;
      const currentColor = this.colors[index];
      if (index === 0) {
        result = `linear-gradient(to right, ${currentColor} ${currentPct}%`;
        prevPct = currentPct;
      } else {
        result = `${result}, ${currentColor} ${prevPct}%, ${currentColor} ${currentPct}%`;
        prevPct = currentPct;
      }
      colorIndex = index;
    });
    const defaultColor = this.colors[colorIndex + 1];
    result = `${result}, ${defaultColor} ${prevPct}%, ${defaultColor} 100%)`;
    return result;
  }

  updateValue(newValue: string | number) {
    log('\n\nestimate-slider.component.updateValue');
    const num =
      typeof newValue === 'string' ? parseInt(newValue, 10) : newValue;
    log('estimate-slider.component.updateValue  ', num);
    if (num < this.min() || num > this.max() || Number.isNaN(num)) {
      console.warn(
        'estimate-slider.component.updateValue  error',
        num,
        this.min(),
        this.max(),
        Number.isNaN(num)
      );
      const errorMessage = this.translateService.instant(
        'main.error_messages.error_commission_estimate_invalid',
        {
          low: this.min(),
          high: this.max(),
        }
      );
      this.error.set(errorMessage);
      return;
    }
    if (this.error() !== null) {
      this.error.set(null);
    }
    this.value.set(Math.round(num));
    this.onChange(this.value());
    log('         new value', this.value());
    const pct = this.getLinePosition(this.value());
    log('         pct', pct);
    this.labelStyles.set({
      left: this.width() * pct + 8 + 'px',
    });
    log('         new styles', this.labelStyles(), '\n\n');
  }

  public editValue(event: MouseEvent) {
    const span: HTMLSpanElement = event.target as HTMLSpanElement;
    span.classList.add('hidden');
    const input = this.valueInput.nativeElement;
    input.classList.remove('hidden');
    input.focus();
    input.addEventListener('blur', () => {
      const value = input.value;
      this.updateValue(value);
      span.classList.remove('hidden');
      input.classList.add('hidden');
    });
  }

  private getMarkStyle(ratio: number, index: number): any {
    const left = `${Math.round(this.width() * ratio) + PADDING}px`;
    let display = 'block';
    if (index === this.tiers().length - 1) {
      display = 'none';
    }
    return { left, display };
  }

  private getLinePosition(num: number): number {
    return (num - this.min()) / (this.max() - this.min());
  }

  onEnterKey(event: Event) {
    const input = event.target as HTMLInputElement;
    // const value = (event.target as HTMLInputElement).value;
    // this.updateValue(value);
    input.blur();
  }
}
